import { useState, useEffect } from 'react';
import { Plus, Trash2, Edit2, Save, X, AlertCircle, Info, CheckCircle, AlertTriangle, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import GlowCard from '@/components/GlowCard';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { useRateLimit, RATE_LIMIT_CONFIGS } from '@/hooks/useRateLimit';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';
import { Navigate } from 'react-router-dom';

interface Announcement {
  id: string;
  title: string;
  content: string;
  type: 'info' | 'success' | 'warning' | 'danger';
  is_active: boolean;
  created_at: string;
}

const typeConfig = {
  info: { icon: Info, color: 'text-primary', label: 'Info' },
  success: { icon: CheckCircle, color: 'text-success', label: 'Success' },
  warning: { icon: AlertTriangle, color: 'text-warning', label: 'Warning' },
  danger: { icon: AlertCircle, color: 'text-destructive', label: 'Danger' },
};

const AdminAnnouncements = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  
  // Rate limiting for admin operations
  const { isLimited, recordRequest, formatRemainingTime } = useRateLimit(RATE_LIMIT_CONFIGS.adminOperations);
  
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    type: 'info' as const,
  });

  useEffect(() => {
    checkAdminStatus();
  }, [user]);

  const checkAdminStatus = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    const { data, error } = await supabase
      .from('profiles')
      .select('is_admin')
      .eq('id', user.id)
      .maybeSingle();

    if (!error && data) {
      setIsAdmin(data.is_admin);
      if (data.is_admin) {
        fetchAnnouncements();
      }
    } else {
      setIsAdmin(false);
    }
    setLoading(false);
  };

  const fetchAnnouncements = async () => {
    const { data, error } = await supabase
      .from('announcements')
      .select('*')
      .order('created_at', { ascending: false });

    if (!error && data) {
      setAnnouncements(data as Announcement[]);
    }
  };

  const handleCreate = async () => {
    if (!formData.title || !formData.content) {
      toast({ title: 'Please fill in all fields', variant: 'destructive' });
      return;
    }

    // Validate input lengths
    if (formData.title.length > 200) {
      toast({ title: 'Title must be 200 characters or less', variant: 'destructive' });
      return;
    }
    if (formData.content.length > 2000) {
      toast({ title: 'Content must be 2000 characters or less', variant: 'destructive' });
      return;
    }

    // Check rate limit
    if (!recordRequest()) {
      toast({ 
        title: 'Rate limit exceeded', 
        description: `Please wait ${formatRemainingTime()} before creating another announcement.`,
        variant: 'destructive' 
      });
      return;
    }

    const { error } = await supabase
      .from('announcements')
      .insert({
        title: formData.title,
        content: formData.content,
        type: formData.type,
        created_by: user?.id,
      });

    if (error) {
      toast({ title: 'Error creating announcement', description: error.message, variant: 'destructive' });
    } else {
      toast({ title: 'Announcement created!' });
      setFormData({ title: '', content: '', type: 'info' });
      setShowForm(false);
      fetchAnnouncements();
    }
  };

  const handleToggleActive = async (id: string, isActive: boolean) => {
    const { error } = await supabase
      .from('announcements')
      .update({ is_active: isActive })
      .eq('id', id);

    if (!error) {
      setAnnouncements(prev => 
        prev.map(a => a.id === id ? { ...a, is_active: isActive } : a)
      );
    }
  };

  const handleDelete = async (id: string) => {
    // Check rate limit
    if (!recordRequest()) {
      toast({ 
        title: 'Rate limit exceeded', 
        description: `Please wait ${formatRemainingTime()} before performing another action.`,
        variant: 'destructive' 
      });
      return;
    }

    const { error } = await supabase
      .from('announcements')
      .delete()
      .eq('id', id);

    if (!error) {
      toast({ title: 'Announcement deleted' });
      fetchAnnouncements();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Announcements</h1>
          <p className="text-muted-foreground">Manage system announcements</p>
        </div>
        <Button onClick={() => setShowForm(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          New Announcement
        </Button>
      </div>

      {/* Create Form */}
      {showForm && (
        <GlowCard>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Create Announcement</h3>
              <Button variant="ghost" size="icon" onClick={() => setShowForm(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            <div className="grid gap-4">
              <div className="grid gap-2">
                <Label>Title <span className="text-muted-foreground text-xs">({formData.title.length}/200)</span></Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value.slice(0, 200) }))}
                  placeholder="Announcement title..."
                  maxLength={200}
                />
              </div>
              
              <div className="grid gap-2">
                <Label>Content <span className="text-muted-foreground text-xs">({formData.content.length}/2000)</span></Label>
                <Textarea
                  value={formData.content}
                  onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value.slice(0, 2000) }))}
                  placeholder="Announcement content..."
                  rows={4}
                  maxLength={2000}
                />
              </div>
              
              <div className="grid gap-2">
                <Label>Type</Label>
                <Select 
                  value={formData.type} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, type: v as any }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(typeConfig).map(([key, config]) => (
                      <SelectItem key={key} value={key}>
                        <div className="flex items-center gap-2">
                          <config.icon className={cn("h-4 w-4", config.color)} />
                          {config.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <Button onClick={handleCreate} className="w-full gap-2">
                <Save className="h-4 w-4" />
                Create Announcement
              </Button>
            </div>
          </div>
        </GlowCard>
      )}

      {/* Announcements Table */}
      <GlowCard className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Type</TableHead>
              <TableHead>Title</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Active</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {announcements.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  No announcements yet
                </TableCell>
              </TableRow>
            ) : (
              announcements.map((announcement) => {
                const config = typeConfig[announcement.type];
                return (
                  <TableRow key={announcement.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <config.icon className={cn("h-4 w-4", config.color)} />
                        <span className="capitalize text-sm">{announcement.type}</span>
                      </div>
                    </TableCell>
                    <TableCell className="font-medium max-w-[200px] truncate">
                      {announcement.title}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {formatDistanceToNow(new Date(announcement.created_at), { addSuffix: true })}
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={announcement.is_active}
                        onCheckedChange={(checked) => handleToggleActive(announcement.id, checked)}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(announcement.id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </GlowCard>
    </div>
  );
};

export default AdminAnnouncements;
